import { IdTokenResult, ParsedToken, User } from 'firebase/auth';
import { createStoreWithSelector } from '../../utils/zustand/createStoreWithSelector';
import { createUseStore } from '../../utils/zustand/createUseStore';

interface CustomClaims extends ParsedToken {
  offline?: boolean;
}

export interface UserSession extends Omit<IdTokenResult, 'claims'> {
  claims: CustomClaims;
}

const initialValue = {
  token: null,
  customClaims: {},
  isAuthResolved: false,
  authUser: null
};

type State = {
  token: string | null;
  customClaims: CustomClaims;
  isAuthResolved: boolean;
  authUser: User | null;
};

type Actions = {
  updateUser(user: null): void;
  updateUser(user: User): Promise<void>;
  reset: () => void;
  setCustomClaims: (claims: CustomClaims) => void;
  getRefreshToken: () => Promise<string | null>;
};

export const globalAuthStore = createStoreWithSelector<State & Actions>()((set, get) => ({
  ...initialValue,
  updateUser: async (user: User | null) => {
    if (!user) {
      set({ authUser: null, token: null, isAuthResolved: true });
      return;
    }

    const tokenResult = await user.getIdTokenResult();
    set({
      authUser: user,
      token: tokenResult.token,
      customClaims: tokenResult.claims,
      isAuthResolved: true
    });
  },
  reset: () => set(initialValue),
  setCustomClaims: (customClaims) => set({ customClaims }),
  getRefreshToken: async () => {
    const { authUser } = get();
    if (!authUser) return null;
    const tokenResult = await authUser.getIdTokenResult(true);
    set({ token: tokenResult.token, customClaims: tokenResult.claims });
    return tokenResult.token;
  }
}));

export const useGlobalAuthStore = createUseStore(globalAuthStore);
export const authStoreActions = (): Actions => globalAuthStore.getState();
